import React, {
  createContext,
  useContext,
  type PropsWithChildren,
} from "react";

import { atom, createStore, useAtom } from "jotai";

import {
  type FetchCreatives_me_creatives_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

export const creativeAnalyticsScope = "creative-analytics";

const CreativeAnalyticsContext = createContext<ReturnType<
  typeof createStore
> | null>(null);
const useCreativeAnalyticsContext = () => {
  const context = useContext(CreativeAnalyticsContext);
  if (!context) {
    throw new Error("Creative analytics context not available");
  }
  return context;
};
const store = createStore();

export const CreativeAnalyticsProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  return (
    <CreativeAnalyticsContext.Provider value={store}>
      {children}
    </CreativeAnalyticsContext.Provider>
  );
};

const graphTypeMetricsAtom = atom<"bar" | "line">("bar");
export const useGraphTypeAtom = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(graphTypeMetricsAtom, { store });
};

const collapseTopBarMetricsAtom = atom(true);
export const useCollapseTopBar = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(collapseTopBarMetricsAtom, { store });
};

const numberOfAdAtom = atom(1);
export const useCurrentNumberOfAdsAtom = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(numberOfAdAtom, { store });
};

const availableMetricsById = atom<Record<string, SalesPageMetric>>({});
export const useAvailableMetricsByIdAtom = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(availableMetricsById, { store });
};

const previewAdAtom = atom<FetchCreatives_me_creatives_rows | null>(null);
export const usePreviewAdAtom = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(previewAdAtom, { store });
};

export type AdColouring = {
  id: string;
  borderColor: string;
  backgroundColor: string;
  pointBackgroundColor: string;
  pointBorderColor: string;
};
export const allAdColours = [
  {
    id: "colour1",
    borderColor: "#B6B4F7",
    backgroundColor: "#B6B4F7",
    pointBackgroundColor: "#514FB2",
    pointBorderColor: "#514FB2",
  },
  {
    id: "colour2",
    borderColor: "#E9A8DC",
    backgroundColor: "#E9A8DC",
    pointBackgroundColor: "#A1077F",
    pointBorderColor: "#A1077F",
  },
  {
    id: "colour3",
    borderColor: "#FFEAAB",
    backgroundColor: "#FFEAAB",
    pointBackgroundColor: "#F3971A",
    pointBorderColor: "#F3971A",
  },
  {
    id: "colour4",
    borderColor: "#7081FF",
    backgroundColor: "#7081FF",
    pointBackgroundColor: "#4454FF",
    pointBorderColor: "#4454FF",
  },
  {
    id: "colour5",
    borderColor: "#A3F9A1",
    backgroundColor: "#A3F9A1",
    pointBackgroundColor: "#8BF788",
    pointBorderColor: "#8BF788",
  },
  {
    id: "colour6",
    borderColor: "#71D5D5",
    backgroundColor: "#71D5D5",
    pointBackgroundColor: "#32B2B2",
    pointBorderColor: "#32B2B2",
  },
];
const availableAdColours = atom<AdColouring[]>(allAdColours);
export const useAvailableAdColoursAtom = () => {
  const store = useCreativeAnalyticsContext();
  return useAtom(availableAdColours, { store });
};
